import { useState, type FormEvent } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, CheckCircle2, ShieldAlert } from "lucide-react";

const ClinicianInquiryForm = () => {
  const [reviewed, setReviewed] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.currentTarget.reset();
    setReviewed(true);
  };

  return (
    <Card className="p-6 sm:p-10 bg-card border-border/60 shadow-sm max-w-2xl mx-auto">
      <div className="mb-6 space-y-3">
        <div className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-primary">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          Collection paused
        </div>
        <h3 className="font-display text-2xl text-foreground tracking-tight">
          Public clinician inquiry submission is not active.
        </h3>
        <p id="clinician-local-only-note" className="text-[15px] leading-[1.7] text-foreground/85">
          Nothing entered below is transmitted, stored, uploaded, or used for outreach. The fields are a local-only checklist to help you outline a general business question. Do not enter health, medical, patient, credential, license, or other sensitive information.
        </p>
      </div>

      {reviewed && (
        <div
          role="status"
          className="mb-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" aria-hidden="true" />
          <p>Your local draft was cleared. Nothing was sent or stored.</p>
        </div>
      )}

      <form
        onSubmit={onSubmit}
        noValidate
        aria-describedby="clinician-local-only-note"
        data-collection-mode="local-only"
        className="space-y-5"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ci-local-name">Name for your local outline</Label>
            <Input id="ci-local-name" name="name" autoComplete="off" maxLength={120} />
          </div>
          <div>
            <Label htmlFor="ci-local-role">Role or team</Label>
            <Input id="ci-local-role" name="role" autoComplete="off" maxLength={120} />
          </div>
        </div>

        <div>
          <Label htmlFor="ci-local-interest">General topic</Label>
          <select
            id="ci-local-interest"
            name="interest"
            defaultValue="product_information"
            className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="product_information">Product information</option>
            <option value="data_handling">Data-handling questions</option>
            <option value="future_research">Future research information</option>
            <option value="other">Other general business question</option>
          </select>
        </div>

        <div>
          <Label htmlFor="ci-local-question">Local notes</Label>
          <Textarea
            id="ci-local-question"
            name="question"
            maxLength={1000}
            rows={4}
            autoComplete="off"
            placeholder="Draft a non-sensitive question here. It will not leave this browser."
          />
        </div>

        <Button
          type="submit"
          variant="outline"
          size="lg"
          aria-label="Review local draft — nothing sent; send inquiry is paused"
          className="group"
        >
          Review local draft — nothing sent
          <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </Button>
      </form>

      <div className="mt-7 border-t border-border/60 pt-5 text-sm text-foreground/80">
        <p>
          For a general, non-sensitive business question, use the current clinician line or email channel. Do not include patient, health, medical, credential, or license information.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:gap-5">
          <a
            href="tel:+18887212444"
            aria-label="Call 1-888-721-2444"
            className="inline-flex min-h-11 items-center rounded-md px-2 underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Call 1-888-721-2444
          </a>
          <a
            href="mailto:hello@myelinahealth.com?subject=General%20clinician%20information"
            className="inline-flex min-h-11 items-center rounded-md px-2 underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Email a general business question
          </a>
          <a
            href="/for-clinicians/data-handling"
            className="inline-flex min-h-11 items-center rounded-md px-2 underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Review the data-handling hold
          </a>
        </div>
      </div>
    </Card>
  );
};

export default ClinicianInquiryForm;
